import React, {useEffect, useState} from 'react'
import {v4 as uuid} from 'uuid'
import moment from 'moment'
import {
	QuoteData,
	QuotePriceData,
	RefundableOptions,
	SaleData,
	SaleResponse,
	useChangeEventArgs,
	useQuoteRequestStatus,
	useRefundableActions
} from 'protect-group-pulse-react'
import FormInput from '../ui/FormInput'
import {sessionKeys} from '../../data'
import Button from '../ui/Button'
import EventArg from '../ui/EventArg'

interface TestFormProps {
	options: RefundableOptions
}

const data: QuoteData = {
	eventTravelDateTime: moment().add(1, 'd').format('DD/MM/YYYY'),
	numberOfTickets: 1,
	totalValue: 10
}

const TestForm = (props: TestFormProps) => {
	const eventArgs = useChangeEventArgs()
	const status = useQuoteRequestStatus()
	const {options} = props
	const [quoteData, setQuoteData] = useState<QuoteData>(data)
	const [apiSuccessResponse, setApiSuccessResponse] = useState<SaleResponse>({} as SaleResponse)
	const [apiErrorResponse, setApiErrorResponse] = useState<any>('')
	const [bookingReference, setBookingReference] = useState<string | null>(null)
	const [requestInProgress, setRequestInProgress] = useState<boolean>(false)
	const [customerServiceEmail, setCustomerServiceEmail] = useState<string | undefined>(undefined)
	const {writeSale, updateQuoteData, saleComplete} = useRefundableActions()

	const eventDate = moment(quoteData.eventTravelDateTime, options.eventDateFormat).format('yyyy-MM-DD')

	useEffect(() => {
		updateQuoteData(data)
	}, [])

	useEffect(() => {
		sessionStorage.setItem(sessionKeys.quoteData, JSON.stringify(quoteData))
	}, [quoteData])

	useEffect(() => {
		sessionStorage.setItem(sessionKeys.eventArgs, JSON.stringify(eventArgs))
		console.log('change event args', eventArgs)
	}, [eventArgs])

	const parseDate = (value: string) => moment(value, 'yyyy-MM-DD').format(options.eventDateFormat)

	const clearResponse = () => {
		setApiSuccessResponse({} as SaleResponse)
		setApiErrorResponse('')
	}

	const handleOptionsChange = (option: string, parse?: (val: any) => any) => (event: any) => {
		let value = event.target.value

		if (parse) {
			value = parse(value)
		}

		setQuoteData(curr => ({...curr, [option]: value}))
	}

	const handleUpdateQuote = () => {
		updateQuoteData(quoteData)
	}

	const handleApiClick = async () => {
		clearResponse()
		setRequestInProgress(true)
		const bookingReference = uuid()
		const request: SaleData = {
			bookingReference: bookingReference,
			customerServiceEmail,
			customers: [
				{firstName: 'Bat', lastName: 'Man'}
			]
		}

		try {
			const response = await writeSale(request)
			setApiSuccessResponse(response)
			setBookingReference(bookingReference)
			sessionStorage.setItem(sessionKeys.bookingReference, bookingReference)
		} catch (e: any) {
			setApiErrorResponse(e)
		} finally {
			setRequestInProgress(false)
		}
	}

	const handleClearResponseClick = () => clearResponse()

	return (
		<div>
			<div className="flex flex-col md:flex-row">
				<div className="flex flex-col w-1/2 sm:w-full">
					<h4 className="mb-4 text-left">Quote Data</h4>

					<FormInput
						id="bookingCost"
						inputProps={{
							min: 0
						}}
						label="Booking Cost"
						onChange={handleOptionsChange('totalValue', parseFloat)}
						type="number"
						value={quoteData.totalValue}
					/>

					<FormInput
						id="eventDate"
						label="Event Date"
						onChange={handleOptionsChange('eventTravelDateTime', parseDate)}
						type="date"
						value={eventDate}
					/>

					<FormInput
						id="numberOfTickets"
						label="Tickets"
						onChange={handleOptionsChange('numberOfTickets', parseInt)}
						type="number"
						value={quoteData.numberOfTickets}
					/>

					<FormInput
						id="displayCurrency"
						label="Display Currency Code"
						onChange={handleOptionsChange('displayCurrencyCode')}
						value={quoteData.displayCurrencyCode}
					/>

					<FormInput
						id="salesTax"
						inputProps={{
							min: 0
						}}
						label="Sales Tax"
						onChange={handleOptionsChange('salesTax', parseFloat)}
						type="number"
						value={quoteData.salesTax}
					/>

					<FormInput
						id="customerServiceEmail"
						label="Customer Service Email"
						onChange={(event) => setCustomerServiceEmail(event.target.value)}
						value={customerServiceEmail ?? ''}
					/>

					<Values
						add={(item) => setQuoteData(curr => ({
							...curr,
							values: [...(curr.values ?? []), item]
						}))}
						remove={(item) => setQuoteData(curr => ({
							...curr,
							values: curr.values?.filter(x => x.reference !== item.reference)
						}))}
						values={quoteData.values ?? []}/>

					<h4>Status: {status}</h4>

				</div>

				<div className="flex flex-col pl-2 w-1/2 sm:w-full">
					<h4>Change event args</h4>
					<EventArgs/>
				</div>
			</div>

			<div className="w-full flex">
				<Button label="Update Quote" onClick={handleUpdateQuote}/>
				<Button label="Write Transaction" onClick={handleApiClick} disabled={requestInProgress}/>
				<Button label="Clear Response" onClick={handleClearResponseClick} disabled={requestInProgress}/>
				<Button label="Reset" onClick={saleComplete}/>
			</div>

			<div>
				{
					bookingReference && (
						<div>
							<p><strong>Booking Reference:</strong> {bookingReference}</p>
						</div>
					)
				}
				{
					Object.keys(apiSuccessResponse).length > 0 && (
						<div>
							<h4 className="pb-2">Request successful</h4>
							<p><strong>Sale Id</strong></p>
							<p className="pb-2">{apiSuccessResponse.saleId}</p>
							<p><strong>Refundable Link</strong></p>
							<p className="pb-2">{apiSuccessResponse.refundableLink}</p>
							<p><strong>Confirmation Html</strong></p>
							<div dangerouslySetInnerHTML={{__html: apiSuccessResponse.refundableConfirmationHtml ?? ''}}></div>
						</div>
					)
				}
				{
					apiErrorResponse && (
						<div>{apiErrorResponse}</div>
					)
				}
			</div>
		</div>
	)
}

export default TestForm

const EventArgs = () => {
	const eventArgs = useChangeEventArgs()
	return (
		<div>
			{
				(eventArgs?.products ?? [])
					.map((product) => {
						return (
							<div key={product.productId}>
								<EventArg label="Product Code" value={product.productCode}/>
								<EventArg label={product.label || 'Value'} value={product.formattedValue}/>
								<EventArg label="Value Net" value={product.formattedValueNet}/>
								<EventArg label="Value Tax" value={product.formattedValueTax}/>
								<EventArg label="Display Value" value={product.formattedDisplayValue}/>
								<EventArg label="Sold" value={product.sold?.toString()}/>
								{
									product.productData.length > 0 && (
										<h5>Product Data</h5>
									)
								}
								{
									product.productData.map((productData) => (
										<div
											className="border-b-blue-500 pl-2 py-2"
											key={`product-data-${productData.totalProductPrice.toString()}`}
										>
											<EventArg label="Product Code" value={productData.productCode}/>
											<EventArg label="Sold" value={productData.sold?.toString()}/>
											<EventArg label="Reference" value={productData.reference}/>
											<EventArg label="Source Price" value={productData.sourcePrice}/>
											<EventArg label="Product Price" value={productData.productPrice}/>
											<EventArg label="Total Price" value={productData.totalProductPrice}/>
											<EventArg label="Total Price Net" value={productData.totalProductPriceNet}/>
											<EventArg label="Total Price Tax" value={productData.totalProductPriceTax}/>
										</div>
									))
								}
							</div>
						)
					})
			}
			<div className="mt-3">
				<EventArg label="Booking Cost" value={eventArgs?.formattedBookingCost}/>
				<EventArg label="Product Value" value={eventArgs?.formattedProductValue}/>
				<EventArg label="Total Value" value={eventArgs?.formattedTotalValue}/>
				<EventArg label="Display Booking Cost" value={eventArgs?.formattedDisplayBookingCost}/>
				<EventArg label="Display Product Value" value={eventArgs?.formattedDisplayProductValue}/>
				<EventArg label="Display Total Value" value={eventArgs?.formattedDisplayTotalValue}/>
			</div>
		</div>
	)
}

type ValuesProps = {
	add: (item: QuotePriceData) => void,
	remove: (item: QuotePriceData) => void,
	values: QuotePriceData[],
}

const Values = (props: ValuesProps) => {
	return (
		<div>
			<div>
				<ValueForm add={props.add}/>
			</div>
			<div>
				{
					props.values.map((item) => (
						<div>
							<div>
								<ValueRow key={item.reference} value={item}/>
							</div>
							<div className="flex justify-end">
								<Button label="Remove" onClick={() => props.remove(item)}/>
							</div>
						</div>
					))
				}
			</div>
		</div>
	)
}

type ValueFormProps = {
	add: (item: QuotePriceData) => void
}

const ValueForm = (props: ValueFormProps) => {
	const [value, setValue] = useState<QuotePriceData>({} as QuotePriceData)
	const handleChange = (option: string, parse?: (val: any) => any) => (event: any) => {
		let value = event.target.value

		if (parse) {
			value = parse(value)
		}

		setValue(curr => ({...curr, [option]: value}))
	}

	return (
		<div>
			<h4>Add Value</h4>
			<div>
				<FormInput
					id="value_reference"
					label="Reference"
					onChange={handleChange('reference')}
					value={value.reference ?? ''}
				/>
				<FormInput
					id="value_numberOfTickets"
					label="Tickets"
					onChange={handleChange('numberOfTickets', parseInt)}
					type="number"
					value={value.numberOfTickets ?? 0}
				/>
				<FormInput
					id="value_salesTax"
					inputProps={{
						min: 0
					}}
					label="Sales Tax"
					onChange={handleChange('salesTax', parseFloat)}
					type="number"
					value={value.salesTax ?? 0}
				/>
				<FormInput
					id="value_value"
					inputProps={{
						min: 0
					}}
					label="Value"

					onChange={handleChange('value', parseFloat)}
					type="number"
					value={value.value ?? 0}
				/>
			</div>
			<div className="flex justify-end">
				<Button label="Add" onClick={() => {
					props.add(value)
					setValue({} as QuotePriceData)
				}}/>
			</div>
		</div>
	)
}

type ValueRowProps = {
	value: QuotePriceData
}

const ValueRow = (props: ValueRowProps) => {
	return (
		<div>
			{
				Object.keys(props.value).map((x: string, i: number) => {
					const key = x as keyof typeof props.value
					return (
						<EventArg key={`${x}`} label={x} value={props.value[key]}/>
					)
				})
			}
		</div>
	)
}
